import { useEffect, useState } from "react";
import api from "../lib/api";
import { X, Search, Send, Loader2, CheckCircle2, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Send a reel to selected Hifireels users via in-platform DM.
 * Wraps its own picker (rather than reusing UserPickerSheet) so we can add a
 * message textarea in-line and a distinct "Send" CTA copy.
 *
 * Props:
 *  - reel: reel object (id, thumbnail_url, title, user)
 *  - onClose()
 *  - onSent?({ sent: number, earned: number })
 */
export default function SendReelSheet({ reel, onClose, onSent }) {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState({});
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentInfo, setSentInfo] = useState(null); // { count, earned }
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!reel) return;
    setLoading(true);
    const t = setTimeout(() => {
      api.get(`/users/search?q=${encodeURIComponent(q.trim() || "a")}&limit=30`)
        .then(r => setUsers(r.data || []))
        .catch(() => setUsers([]))
        .finally(() => setLoading(false));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [reel, q]);

  if (!reel) return null;

  const toggle = (u) => setSelected(s => {
    const n = { ...s };
    if (n[u.id]) delete n[u.id]; else n[u.id] = u;
    return n;
  });
  const chosen = Object.values(selected);

  const send = async () => {
    setErr(""); setSending(true);
    try {
      const { data } = await api.post(`/reels/${reel.id}/send`, {
        user_ids: chosen.map(u => u.id),
        message: (msg || "").slice(0, 280),
      });
      setSentInfo({ count: data.sent || chosen.length, earned: data.earned || 0 });
      onSent?.(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not send reel.");
    } finally { setSending(false); }
  };

  return (
    <AnimatePresence>
      <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
        className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center" data-testid="send-reel-sheet">
        <div className="absolute inset-0 bg-black/75" onClick={onClose}/>
        <motion.div initial={{y:"100%"}} animate={{y:0}} exit={{y:"100%"}} transition={{type:"spring", damping:26}}
          className="relative w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl bg-[#0B0F19] border border-white/15 max-h-[85vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00E5FF] to-[#A855F7] flex items-center justify-center"><Send className="w-4 h-4 text-black"/></div>
              <div>
                <h3 className="font-outfit font-extrabold text-lg leading-none">Send to Hifireels friends</h3>
                <p className="text-[10px] text-white/50 mt-1">Share this reel privately via DM</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="send-reel-close" className="p-1.5 rounded-full bg-white/8"><X className="w-4 h-4"/></button>
          </div>

          {/* Reel preview strip */}
          <div className="p-3 border-b border-white/10 flex items-center gap-3">
            <div className="w-14 h-20 rounded-xl bg-black/50 overflow-hidden flex-shrink-0">
              {reel.thumbnail_url ? <img src={reel.thumbnail_url} alt="" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center text-2xl">📹</div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">{reel.title || "Hifireels reel"}</div>
              <div className="text-[11px] text-white/55">by @{reel.user?.username || "creator"}</div>
            </div>
          </div>

          {sentInfo ? (
            <div className="p-6 flex flex-col items-center gap-3 text-center">
              <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-emerald-400"/></div>
              <div className="font-outfit font-extrabold text-xl">Reel sent to {sentInfo.count} {sentInfo.count === 1 ? "friend" : "friends"}!</div>
              {sentInfo.earned > 0 && <div className="text-sm text-[#FF7A00]">🎉 +{sentInfo.earned} Goldly earned for sharing</div>}
              <button onClick={onClose} data-testid="send-reel-done" className="mt-2 px-5 py-2 rounded-full bg-white/10 border border-white/15 text-sm font-bold">Done</button>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="p-3 border-b border-white/10">
                <div className="relative">
                  <Search className="w-4 h-4 text-white/50 absolute left-3 top-1/2 -translate-y-1/2"/>
                  <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search users by @username or name…"
                    data-testid="send-reel-search"
                    className="w-full pl-9 pr-3 py-2.5 rounded-full bg-white/5 border border-white/10 outline-none text-sm focus:border-[#00E5FF]"/>
                </div>
              </div>

              {/* Users list */}
              <div className="flex-1 overflow-y-auto px-2 pb-2" data-testid="send-reel-list">
                {loading ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#00E5FF]"/></div>
                ) : users.length === 0 ? (
                  <div className="text-center py-6 text-white/50 text-sm flex flex-col items-center gap-2"><Users className="w-6 h-6 opacity-40"/>No users found</div>
                ) : users.map(u => {
                  const on = !!selected[u.id];
                  return (
                    <button key={u.id} onClick={() => toggle(u)}
                      data-testid={`send-reel-user-${u.id}`}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-2xl active:scale-[0.99] ${on ? "bg-[#00E5FF]/10 border border-[#00E5FF]/40" : "hover:bg-white/5 border border-transparent"}`}>
                      <img src={u.avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-white/10"/>
                      <div className="flex-1 min-w-0 text-left">
                        <div className="text-sm font-bold truncate">{u.name || u.username}</div>
                        <div className="text-[10px] text-white/50 truncate">@{u.username} · {u.followers || 0} followers</div>
                      </div>
                      {on && <CheckCircle2 className="w-5 h-5 text-[#00E5FF] flex-shrink-0"/>}
                    </button>
                  );
                })}
              </div>

              {/* Message + CTA */}
              <div className="p-3 border-t border-white/10 space-y-2">
                {chosen.length > 0 && (
                  <textarea value={msg} onChange={e => setMsg(e.target.value.slice(0, 280))} rows={2}
                    placeholder="Add a message (optional, 280 chars)…"
                    data-testid="send-reel-msg"
                    className="w-full px-3 py-2 rounded-2xl bg-white/5 border border-white/10 outline-none text-sm placeholder:text-white/40"/>
                )}
                {err && <div className="text-[#FF2A5F] text-xs" data-testid="send-reel-err">{err}</div>}
                <button onClick={send} disabled={chosen.length === 0 || sending}
                  data-testid="send-reel-confirm"
                  className="w-full py-2.5 rounded-full bg-gradient-to-r from-[#00E5FF] to-[#A855F7] text-black font-outfit font-extrabold disabled:opacity-40 active:scale-95 flex items-center justify-center gap-2">
                  {sending ? <><Loader2 className="w-4 h-4 animate-spin"/>Sending…</> : <><Send className="w-4 h-4"/>Send to {chosen.length || 0} {chosen.length === 1 ? "friend" : "friends"}</>}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
